const API_URL = process.env.REACT_APP_API_URL || '/api';

export interface WorkIdTaskSummary {
  id: number;
  status: string;
  type: string;
  name: string | null;
  taskNumber: string | null;
  completionDate?: string | null;
  foremanAreaId?: number | null;
}

export interface WorkIdListItem {
  id: number;
  workIdentificationId: string | null;
  status: string;
  reportedOn: string;
  /** Server row creation time — WRAP uses this as the baseline for “elapsed since created” views; not a contractual SA response-time clock. */
  createdAt?: string;
  updatedAt?: string;
  comments: string | null;
  priority: string | null;
  foremanAreaId: number | null;
  foremanArea: { id: number; name: string } | null;
  reportedBy: { id: number; name: string; lastName: string } | null;
  _count: { tasks: number };
  tasks: WorkIdTaskSummary[];
}

export interface WorkIdHistoryUser {
  id: number;
  name: string;
  lastName: string;
}

export interface WorkIdAttachment {
  id: number;
  attachmentURL: string;
  attachmentName: string;
  attachmentNotes?: string | null;
  attachmentType?: string | null;
}

export interface WorkIdHistory {
  id: number;
  workIdId: number;
  comments: string | null;
  summary: string | null;
  stage: string;
  external: boolean;
  userId: number | null;
  createdAt: string;
  user: WorkIdHistoryUser | null;
}

export interface MergeSuggestionCandidate {
  workIdId: number;
  workIdentificationId: string | null;
  status: string | null;
  summary: string;
  /** Full comments for the target Work ID */
  comments?: string | null;
  /** Linked tasks for the target Work ID */
  tasks?: Array<{
    id: number;
    taskNumber: string | null;
    name: string | null;
    status: string;
    type: string;
  }>;
  reasonSummary: string;
  score: number;
  reportedOn: string;
  reportedBy: string;
  foremanAreaName: string | null;
  location: { lat: number; lng: number } | null;
}

export interface MergeSuggestionGroup {
  defaultParentWorkIdId: number;
  defaultParentWorkIdentificationId: string | null;
  candidates: MergeSuggestionCandidate[];
}

export interface MergeSuggestionsResponse {
  workIdId: number;
  workIdentificationId: string | null;
  sourceLocation: { lat: number; lng: number } | null;
  suggestions: MergeSuggestionGroup[];
}

export interface WorkIdDetail extends WorkIdListItem {
  /** When this Work ID is merged (child), the parent it was merged into. */
  mergedIntoParent?: { id: number; workIdentificationId: string | null };
  /** Work ID GPS when available (from submission); preferred over asset for display/task prepopulation. */
  latitude?: number | null;
  longitude?: number | null;
  /** Estimated accomplishment when available. */
  estimatedAccomplishment?: number | null;
  asset: {
    id: number;
    name: string;
    assetNumber: string;
    startLatitude?: number | null;
    startLongitude?: number | null;
    contractAreaId?: number | null;
  } | null;
  createdFromForm?: { id: number } | null;
  attachments?: WorkIdAttachment[];
  foremanArea: {
    id: number;
    name: string;
    serviceArea: { id: number; name: string; code: string } | null;
  } | null;
  reportedBy: { id: number; name: string; lastName: string; email: string } | null;
  workIdHistory?: WorkIdHistory[];
  tasks: (WorkIdTaskSummary & { completionDate?: string | null; foremanAreaId?: number | null })[];
}

export interface WorkIdFilterOptions {
  serviceAreas: Array<{ id: number; name: string }>;
  foremanAreas: Array<{ id: number; name: string; serviceAreaId: number; serviceAreaName?: string }>;
}

export const workIdService = {
  getList: async (params?: { status?: string; limit?: number }): Promise<WorkIdListItem[]> => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    const url = searchParams.toString() ? `${API_URL}/work-ids?${searchParams}` : `${API_URL}/work-ids`;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) throw new Error('Failed to fetch work IDs');
    const json = await response.json();
    // Handle paginated response from merged endpoint
    return Array.isArray(json) ? json : (json.data ?? []);
  },

  getFilterOptions: async (): Promise<WorkIdFilterOptions> => {
    const response = await fetch(`${API_URL}/work-ids/filter-options`, { credentials: 'include' });
    if (!response.ok) throw new Error('Failed to fetch filter options');
    return response.json();
  },

  getById: async (id: number): Promise<WorkIdDetail> => {
    const response = await fetch(`${API_URL}/work-ids/${id}`, { credentials: 'include' });
    if (!response.ok) throw new Error('Failed to fetch work ID');
    return response.json();
  },

  uploadAttachment: async (
    workIdId: number,
    file: File,
    notes?: string
  ): Promise<WorkIdAttachment> => {
    const formData = new FormData();
    formData.append('file', file);
    if (notes && notes.trim()) {
      formData.append('notes', notes.trim());
    }

    const response = await fetch(`${API_URL}/work-ids/${workIdId}/attachments`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { error?: string; details?: string };
      const msg = body?.error || 'Failed to upload attachment';
      throw new Error(body?.details ? `${msg}: ${body.details}` : msg);
    }

    return response.json();
  },

  create: async (data: {
    foremanAreaId: number;
    comments: string;
    priority: string;
    assetId: number;
    latitude: number;
    longitude: number;
  }): Promise<WorkIdDetail> => {
    const response = await fetch(`${API_URL}/work-ids`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string; details?: string };
      const msg = body?.error || 'Failed to create work ID';
      throw new Error(body?.details ? `${msg}: ${body.details}` : msg);
    }
    return response.json();
  },

  getHistory: async (workIdId: number): Promise<WorkIdHistory[]> => {
    const response = await fetch(`${API_URL}/work-ids/${workIdId}/history`, { credentials: 'include' });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const msg = (body as { error?: string; details?: string })?.error;
      const details = (body as { details?: string })?.details;
      throw new Error(details ? `${msg || 'Failed to fetch work ID history'}: ${details}` : (msg || `Failed to fetch work ID history (${response.status})`));
    }
    return response.json();
  },

  addHistoryComment: async (
    workIdId: number,
    data: { comments: string; userId: number }
  ): Promise<WorkIdHistory> => {
    const response = await fetch(`${API_URL}/work-ids/${workIdId}/history-comment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const msg = (body as { error?: string })?.error;
      const details = (body as { details?: string })?.details;
      throw new Error(details ? `${msg || 'Failed to add comment'}: ${details}` : (msg || `Failed to add comment (${response.status})`));
    }
    return response.json();
  },

  update: async (
    id: number,
    data: Partial<{
      workIdentificationId: string;
      comments: string;
      priority: string;
      foremanAreaId: number;
      assetId: number;
      activityId: number;
      estimatedAccomplishment: number;
      /** YYYY-MM-DD or ISO string; server accepts only from Admin role */
      reportedOn: string;
    }>
  ): Promise<WorkIdDetail> => {
    const response = await fetch(`${API_URL}/work-ids/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const msg = (body as { error?: string })?.error;
      const details = (body as { details?: string })?.details;
      throw new Error(details ? `${msg || 'Failed to update work ID'}: ${details}` : (msg || `Failed to update work ID (${response.status})`));
    }
    return response.json();
  },

  approve: async (
    id: number,
    data?: {
      comments?: string;
      assetId?: number;
    }
  ): Promise<WorkIdDetail> => {
    const response = await fetch(`${API_URL}/work-ids/${id}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data || {}),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string; details?: string };
      const msg = body?.error || 'Failed to approve work ID';
      const details = body?.details;
      throw new Error(details ? `${msg}: ${details}` : msg);
    }
    return response.json();
  },

  cancel: async (id: number, comments: string): Promise<WorkIdDetail> => {
    const response = await fetch(`${API_URL}/work-ids/${id}/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ comments }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string; details?: string };
      const msg = body?.error || 'Failed to cancel work ID';
      throw new Error(body?.details ? `${msg}: ${body.details}` : msg);
    }
    return response.json();
  },

  closeWithoutAction: async (id: number, comments?: string): Promise<WorkIdDetail> => {
    const response = await fetch(`${API_URL}/work-ids/${id}/close-without-action`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(comments != null && comments.trim() !== '' ? { comments: comments.trim() } : {}),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string; details?: string };
      const msg = body?.error || 'Failed to close work ID without action';
      throw new Error(body?.details ? `${msg}: ${body.details}` : msg);
    }
    return response.json();
  },

  reopen: async (id: number, comments?: string): Promise<WorkIdDetail> => {
    const response = await fetch(`${API_URL}/work-ids/${id}/reopen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(comments != null ? { comments } : {}),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string; details?: string };
      const msg = body?.error || 'Failed to reopen work ID';
      throw new Error(body?.details ? `${msg}: ${body.details}` : msg);
    }
    return response.json();
  },

  getMergeSuggestions: async (workIdId: number): Promise<MergeSuggestionsResponse> => {
    const response = await fetch(`${API_URL}/work-ids/${workIdId}/merge-suggestions`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string; details?: string };
      const message = body?.details || body?.error || 'Failed to fetch merge suggestions';
      throw new Error(message);
    }
    return response.json();
  },

  rejectMergeSuggestion: async (workIdId: number, targetWorkIdId: number): Promise<void> => {
    const response = await fetch(`${API_URL}/work-ids/${workIdId}/merge-suggestions/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ targetWorkIdId }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error((body as { error?: string })?.error || 'Failed to reject merge suggestion');
    }
  },

  merge: async (id: number, data?: { targetWorkIdId?: number; comments?: string }): Promise<WorkIdDetail> => {
    const response = await fetch(`${API_URL}/work-ids/${id}/merge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data || {}),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string; details?: string };
      const message = body?.details || body?.error || 'Failed to merge work ID';
      throw new Error(message);
    }
    return response.json();
  },

  /**
   * Unmerge two Work IDs that were merged by mistake. Removes the link and sets the child back to NEW.
   * Either work ID can be passed as id; the other is otherWorkIdId. comments is required.
   */
  unmerge: async (
    id: number,
    data: { otherWorkIdId: number; comments: string }
  ): Promise<WorkIdDetail> => {
    const response = await fetch(`${API_URL}/work-ids/${id}/unmerge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error((body as { error?: string })?.error || 'Failed to unmerge work ID');
    }
    return response.json();
  },

  assign: async (id: number, taskId: number, comments?: string): Promise<WorkIdDetail> => {
    const response = await fetch(`${API_URL}/work-ids/${id}/assign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ taskId, comments }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error((body as { error?: string })?.error || 'Failed to assign work ID');
    }
    return response.json();
  },

  unassign: async (id: number, taskId: number): Promise<WorkIdDetail> => {
    const response = await fetch(`${API_URL}/work-ids/${id}/unassign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ taskId }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string; details?: string };
      const msg = body?.error || 'Failed to remove Work ID from task';
      throw new Error(body?.details ? `${msg}: ${body.details}` : msg);
    }
    return response.json();
  },

  /**
   * Admin-only: regenerate merge suggestions for all eligible Work IDs (NEW, APPROVED, ASSIGNED).
   * Returns { success, processed, total, errors? }.
   */
  bulkMerge: async (
    merges: Array<{ childWorkIdId: number; parentWorkIdId: number; comments?: string }>
  ): Promise<{ success: boolean; merged: number; errors: Array<{ childWorkIdId: number; error: string }> }> => {
    const response = await fetch(`${API_URL}/work-ids/bulk-merge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ merges }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string; details?: string };
      const msg = body?.error || 'Failed to bulk merge work IDs';
      throw new Error(body?.details ? `${msg}: ${body.details}` : msg);
    }
    return response.json();
  },

  getLinkedPcrs: async (workIdId: number): Promise<import('./pcrService').PcrSubmission[]> => {
    const response = await fetch(`${API_URL}/work-ids/${workIdId}/pcr-submissions`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string };
      throw new Error(body?.error || `Failed to fetch linked PCRs (${response.status})`);
    }
    const data = await response.json() as { submissions: import('./pcrService').PcrSubmission[] };
    return data.submissions ?? [];
  },

  regenerateAllMergeSuggestions: async (): Promise<{
    success: boolean;
    processed: number;
    total: number;
    errors?: string[];
  }> => {
    const response = await fetch(`${API_URL}/work-ids/admin/regenerate-merge-suggestions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string; details?: string };
      const msg = body?.error || 'Failed to regenerate merge suggestions';
      throw new Error(body?.details ? `${msg}: ${body.details}` : msg);
    }
    return response.json();
  },
};
